import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MANAGER_LIKE_ROLES, ADMIN_LIKE_ROLES, canEditModule as canEditModuleResolver } from '../config/modules';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantFeatures, setTenantFeatures] = useState({
    recipe_module: true,
    pricing_module: true,
    production_planner: true,
    inventory_module: true,
    cost_insights: true,
  });
  // iter177 — track whether the live tenant-feature payload has loaded.
  // RootRedirect blocks rendering until this is true so we don't bounce a
  // restricted tenant onto Dashboard before the feature payload arrives.
  const [tenantFeaturesLoading, setTenantFeaturesLoading] = useState(true);
  // ITER308-D · Tenant brand (primary colour + logo). Loaded post-auth
  // and stored in state only — never written to `document.documentElement`
  // so guest-facing surfaces (loyalty wallet, gift voucher) keep the
  // platform's studio design. `Layout.js` reads this state and applies
  // it as inline CSS variables on the admin/manager shell root only.
  //
  // `previewPrimaryColor` is a transient in-memory value the branding
  // section uses to render live previews before the admin saves. Cleared
  // on save/reset/logout.
  const [tenantBranding, setTenantBranding] = useState({
    primary_color: null, has_logo: false, tenant_id: null, logo_version: 0,
  });
  const [previewPrimaryColor, setPreviewPrimaryColor] = useState(null);

  // Pass 3 · Public Demo — populated after `/api/public-demo/session`
  // returns is_public_demo_session=true; null for normal internal
  // logins. Read by <PublicDemoBanner /> and the write-guarded axios
  // interceptor to render friendly 403 messages.
  const [publicDemoSession, setPublicDemoSession] = useState(null);

  const _loadPublicDemoSession = useCallback(async (token) => {
    try {
      const res = await axios.get(`${API}/public-demo/session`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.is_public_demo_session) {
        setPublicDemoSession(res.data);
      } else {
        setPublicDemoSession(null);
      }
    } catch {
      setPublicDemoSession(null);
    }
  }, []);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadFeatures = useCallback(async (token) => {
    try {
      const res = await axios.get(`${API}/tenant/features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // iter180b — REPLACE (not merge). Backend returns all registry keys
      // every call, so keeping stale keys in state can mask a feature that
      // a super-admin just disabled. Replace wholesale.
      setTenantFeatures(res.data || {});
    } catch {
      // Default-allow on failure — backend still enforces.
    } finally {
      setTenantFeaturesLoading(false);
    }
  }, []);

  // ITER308-D · Load tenant branding + apply CSS variables. Called after
  // login and on token restore so the shell always renders the tenant's
  // primary colour and logo.
  const loadBranding = useCallback(async (token) => {
    try {
      const res = await axios.get(`${API}/branding/tenant`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || {};
      setTenantBranding((prev) => ({
        primary_color: data.primary_color || null,
        has_logo:      !!data.has_logo,
        tenant_id:     data.tenant_id || null,
        logo_version:  prev.logo_version,
      }));
    } catch {
      /* Non-fatal — CSS defaults in :root take over. */
    }
  }, []);

  const refreshBranding = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setTenantBranding((prev) => ({ ...prev, logo_version: (prev.logo_version || 0) + 1 }));
    await loadBranding(token);
  }, [loadBranding]);

  // iter180b — Auto-refresh tenant feature flags when:
  //   1. Tab regains focus (covers super-admin-toggles-in-another-tab case)
  //   2. Network reconnects (covers offline → online)
  //   3. Periodically while tab is active (60s polling — cheap, single key:bool dict)
  // Without this, an already-signed-in admin will hold a STALE feature set
  // for the entire session and may see a navbar group that should be hidden.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const refreshNow = () => {
      const token = localStorage.getItem('token');
      if (token && alive) loadFeatures(token);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshNow();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', refreshNow);
    window.addEventListener('online', refreshNow);
    const poll = setInterval(refreshNow, 60_000);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', refreshNow);
      window.removeEventListener('online', refreshNow);
      clearInterval(poll);
    };
  }, [user, loadFeatures]);

  // ─── Canonical outlet context ───────────────────────────────────────────
  // Every outlet-scoped role (manager / supervisor / chef / accountant /
  // store_manager / cashier / maintenance) exposes the same shape so any
  // consumer can reason about outlets uniformly:
  //   activeOutlet     → { id, name, rooms_enabled, has_logo } | null
  //   assignedOutlets  → [{ id, name }, ...]
  // Admin / super_admin are NOT outlet-scoped (they get an outlet picker on
  // each page instead) and therefore activeOutlet is null.
  const isOutletScoped = user && MANAGER_LIKE_ROLES.includes(user.role);
  const activeOutlet = (isOutletScoped && user.outlet_id) ? {
    id: user.outlet_id,
    name: user.outlet_name,
    rooms_enabled: user.rooms_enabled,
    has_logo: user.has_logo,
  } : null;
  const assignedOutlets = user?.assigned_outlets || [];

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          const u = res.data;
          // Restore persisted active outlet if valid
          const saved = localStorage.getItem('active_outlet_id');
          if (saved && u.assigned_outlet_ids?.includes(saved) && saved !== u.outlet_id) {
            // Switch to saved outlet silently
            axios.post(`${API}/manager/switch-outlet`, { outlet_id: saved }, { headers: { Authorization: `Bearer ${token}` } })
              .then(switchRes => {
                setUser({
                  ...u,
                  outlet_id: switchRes.data.outlet_id,
                  outlet_name: switchRes.data.outlet_name,
                  rooms_enabled: switchRes.data.rooms_enabled,
                  has_logo: switchRes.data.has_logo,
                  pos_type: switchRes.data.pos_type,
                });
                loadFeatures(token);
                loadBranding(token);
              })
              .catch(() => {
                localStorage.removeItem('active_outlet_id');
                setUser(u);
                loadFeatures(token);
                loadBranding(token);
              })
              .finally(() => setLoading(false));
          } else {
            if (u.outlet_id) localStorage.setItem('active_outlet_id', u.outlet_id);
            setUser(u);
            loadFeatures(token);
            loadBranding(token);
            _loadPublicDemoSession(token);
            setLoading(false);
          }
        })
        .catch(() => { localStorage.removeItem('token'); localStorage.removeItem('active_outlet_id'); setUser(null); setLoading(false); setTenantFeaturesLoading(false); });
    } else {
      setLoading(false);
      setTenantFeaturesLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    setTenantFeaturesLoading(true);
    const u = res.data.user;
    // Restore persisted active outlet or default to first
    const saved = localStorage.getItem('active_outlet_id');
    if (saved && u.assigned_outlet_ids?.includes(saved) && saved !== u.outlet_id) {
      try {
        const switchRes = await axios.post(`${API}/manager/switch-outlet`, { outlet_id: saved }, { headers: { Authorization: `Bearer ${res.data.token}` } });
        u.outlet_id = switchRes.data.outlet_id;
        u.outlet_name = switchRes.data.outlet_name;
        u.rooms_enabled = switchRes.data.rooms_enabled;
        u.has_logo = switchRes.data.has_logo;
        u.pos_type = switchRes.data.pos_type;
      } catch {
        localStorage.removeItem('active_outlet_id');
      }
    } else if (u.outlet_id) {
      localStorage.setItem('active_outlet_id', u.outlet_id);
    }
    setUser(u);
    loadFeatures(res.data.token);
    loadBranding(res.data.token);
    return u;
  };

  const logout = () => {
    // Pass 3 · Public Demo — best-effort server-side invalidation so
    // the same JWT stops working on subsequent GETs.
    const token = localStorage.getItem('token');
    if (publicDemoSession && token) {
      axios.post(`${API}/public-demo/logout`, {},
        { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    localStorage.removeItem('token');
    localStorage.removeItem('active_outlet_id');
    localStorage.removeItem('pos_token');
    setUser(null);
    setTenantFeatures({});
    setTenantFeaturesLoading(false);
    setTenantBranding({ primary_color: null, has_logo: false, tenant_id: null, logo_version: 0 });
    setPreviewPrimaryColor(null);
    setPublicDemoSession(null);
  };

  const switchOutlet = async (outletId) => {
    const token = localStorage.getItem('token');
    const res = await axios.post(`${API}/manager/switch-outlet`, { outlet_id: outletId }, { headers: { Authorization: `Bearer ${token}` } });
    localStorage.setItem('active_outlet_id', res.data.outlet_id);
    setUser(prev => ({
      ...prev,
      outlet_id: res.data.outlet_id,
      outlet_name: res.data.outlet_name,
      rooms_enabled: res.data.rooms_enabled,
      has_logo: res.data.has_logo,
    }));
    return res.data;
  };

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(prev => ({ ...(prev || {}), ...data }));
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  const assignedModules = user?.assigned_modules || [];
  // Single capability resolver — mirrors backend services/permissions.py
  // exactly so frontend and backend agree on every edit decision.
  const canEditModule = useCallback((module, outletId = null) => {
    return canEditModuleResolver(user, module, outletId);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getHeaders, API, activeOutlet, assignedOutlets, switchOutlet, canEditModule, tenantFeatures, tenantFeaturesLoading, reloadFeatures: () => loadFeatures(localStorage.getItem('token')), refresh, tenantBranding, refreshBranding, previewPrimaryColor, setPreviewPrimaryColor, publicDemoSession, isPublicDemoSession: !!publicDemoSession, setPublicDemoSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
